import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Browser-native speech-to-text dictate button.
 *
 * Uses Web Speech API (`SpeechRecognition`) — works offline-free in Chrome / Edge / Safari iOS 14+.
 * No external key required.
 *
 * Props:
 *   value      — current text in the bound field
 *   onChange   — called with the updated string (existing + " " + recognized text)
 *   disabled   — disable while a field is locked / read-only
 *   testId     — base data-testid for QA (e.g. "mic-disch-final_diagnosis")
 *   lang       — recognition language tag, default "en-IN"
 *   compact    — when true, smaller pill button
 */
export default function MicDictateButton({ value = "", onChange, disabled = false, testId = "mic-btn", lang = "en-IN", compact = false }) {
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const finalRef = useRef("");  // text confirmed at the moment we started

  // Capability detection
  const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const supported = !!SR;

  useEffect(() => {
    return () => {
      // cleanup on unmount
      try { recRef.current?.stop(); } catch (_) { /* ignore */ }
    };
  }, []);

  const stop = () => {
    try { recRef.current?.stop(); } catch (_) { /* ignore */ }
    setListening(false);
  };

  const start = () => {
    if (!supported) {
      toast.error("Voice dictation isn't supported in this browser. Try Chrome or Edge.");
      return;
    }
    if (listening) { stop(); return; }
    finalRef.current = value || "";
    const rec = new SR();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      let finalChunk = "";
      let interimChunk = "";
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const txt = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalChunk += txt + " ";
        else interimChunk += txt;
      }
      if (finalChunk) {
        finalRef.current = `${finalRef.current}${finalRef.current && !finalRef.current.endsWith(" ") ? " " : ""}${finalChunk.trim()}`;
        onChange?.(finalRef.current);
      } else if (interimChunk) {
        // Show interim text appended to the persisted final value (without committing)
        onChange?.(`${finalRef.current}${finalRef.current && !finalRef.current.endsWith(" ") ? " " : ""}${interimChunk}`);
      }
    };
    rec.onerror = (ev) => {
      if (ev.error === "not-allowed" || ev.error === "service-not-allowed") {
        toast.error("Microphone access denied. Allow mic in browser settings to use dictation.");
      } else if (ev.error !== "aborted" && ev.error !== "no-speech") {
        toast.error(`Speech recognition error: ${ev.error}`);
      }
      setListening(false);
    };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    try {
      rec.start();
      setListening(true);
      toast.info("Listening… click the mic again to stop.", { duration: 2000 });
    } catch (_) {
      setListening(false);
    }
  };

  const sizeCls = compact ? "h-7 px-2 text-[10px]" : "h-8 px-2.5 text-xs";
  return (
    <button
      type="button"
      onClick={start}
      disabled={disabled || !supported}
      title={supported ? (listening ? "Stop dictation" : "Dictate (speech-to-text)") : "Voice dictation unsupported in this browser"}
      className={`inline-flex items-center gap-1.5 rounded-full border font-semibold transition ${sizeCls} ${
        listening
          ? "border-rose-300 bg-rose-50 text-rose-700 animate-pulse"
          : supported
            ? "border-brand/30 bg-brand/5 text-brand hover:bg-brand/10"
            : "border-border bg-secondary text-ink-subtle cursor-not-allowed"
      }`}
      data-testid={testId}
    >
      {listening ? <MicOff size={compact ? 11 : 13} /> : <Mic size={compact ? 11 : 13} />}
      {listening ? "Listening…" : "Dictate"}
    </button>
  );
}
